import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Badge, Button, Card, Spinner } from 'react-bootstrap';
import { ArrowLeft, GeoAlt, Clock, Person, Telephone, CaretLeft, CaretRight, CartPlus, CheckCircle, Trash } from 'react-bootstrap-icons';
import { Canvas } from "@react-three/fiber";
import { Stage } from "@react-three/drei";
import axios from 'axios';
import { Model } from '../Model/Model';
import { accidentImages } from '../../data/mockLeads';

function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'week', seconds: 604800 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 }
  ];

  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
    }
  }
  return 'Just now';
}

function getSeverityColor(severity) {
  if (severity >= 7) return 'danger';
  if (severity >= 4) return 'warning';
  return 'success';
}

export function LeadDetail({ lead, onBack, idToken, isAdmin, onPurchase, onDelete, alreadyPurchased = false }) {
  const ref = useRef();
  const [showFront, setShowFront] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [purchased, setPurchased] = useState(alreadyPurchased);
  const [error, setError] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    if (!lead?.photoUrl || !idToken) { setPhotoUrl(null); return; }
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_CORE_API}/lead/${lead.id}/photo`, { headers: { Authorization: idToken } })
      .then(res => { if (!cancelled) setPhotoUrl(res.data.url); })
      .catch(() => { if (!cancelled) setPhotoUrl(null); });
    return () => { cancelled = true; };
  }, [lead?.id, lead?.photoUrl, idToken]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    try {
      setDeleting(true);
      await axios.delete(`${process.env.REACT_APP_CORE_API}/lead/${lead.id}`, { headers: { Authorization: idToken } });
      onDelete?.(lead.id);
      onBack();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data || 'Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  };

  const handlePurchase = async () => {
    if (!idToken || purchasing) return;

    try {
      setPurchasing(true);
      setError(null);
      await axios.post(
        `${process.env.REACT_APP_CORE_API}/lead/${lead.id}/purchase`,
        {},
        { headers: { Authorization: idToken } }
      );
      setPurchased(true);
      onPurchase?.();
    } catch (err) {
      console.error('Failed to purchase lead:', err);
      setError(err.response?.data || 'Failed to purchase lead');
    } finally {
      setPurchasing(false);
    }
  };

  if (!lead) {
    return (
      <Container className="py-5 text-center">
        <h2>Lead not found</h2>
        <Button variant="link" onClick={onBack}>Back to Marketplace</Button>
      </Container>
    );
  }

  const preSelectedParts = lead.injuries.map(i => i.materialName);
  const maxSeverity = Math.max(...lead.injuries.map(i => i.severity));
  const accidentImage = accidentImages[lead.accidentType] || accidentImages["Car Accident"];

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return (
    <div className="lead-detail-page">
      <Container className="py-4">
        <Button variant="link" onClick={onBack} className="text-decoration-none mb-4 ps-0">
          <ArrowLeft size={20} className="me-2" />
          Back to Marketplace
        </Button>

        <Row>
          <Col lg={6} className="mb-4">
            <Card className="lead-detail-model-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Injury Location</h5>
                  <div className="d-flex gap-2">
                    <button
                      className="rotate-btn-sm"
                      onClick={() => setShowFront(!showFront)}
                      aria-label="Rotate left"
                    >
                      <CaretLeft size={24} />
                    </button>
                    <button
                      className="rotate-btn-sm"
                      onClick={() => setShowFront(!showFront)}
                      aria-label="Rotate right"
                    >
                      <CaretRight size={24} />
                    </button>
                  </div>
                </div>

                <div style={{ height: '400px', width: '100%' }}>
                  <Canvas shadows dpr={[1, 2]} camera={{ fov: 50 }}>
                    <Suspense fallback={null}>
                      <Stage controls={ref} preset="rembrandt" intensity={2} contactShadow={false} environment="studio">
                        <Model
                          showFront={showFront}
                          selectedBodyParts={[]}
                          setSelectedBodyParts={() => {}}
                          readOnly={true}
                          preSelectedParts={preSelectedParts}
                        />
                      </Stage>
                    </Suspense>
                  </Canvas>
                </div>
              </Card.Body>
            </Card>

            <Card className="lead-detail-photo-card mt-4">
              <Card.Body>
                <h5 className="mb-3">Accident Photo</h5>
                <img
                  src={photoUrl || accidentImage}
                  alt={lead.accidentType}
                  className="lead-detail-photo"
                />
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="lead-detail-info-card mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h4 className="mb-0">Injury Details</h4>
                  <Badge bg={getSeverityColor(maxSeverity)} className="lead-severity-badge-lg">
                    Max Severity {maxSeverity}/10
                  </Badge>
                </div>

                <div className="lead-injuries-list mb-4">
                  {lead.injuries.map((injury, idx) => (
                    <div key={idx} className="lead-injury-item d-flex justify-content-between align-items-center py-2 border-bottom">
                      <div>
                        <span className="fw-bold">{injury.bodyPart}</span>
                      </div>
                      <Badge bg={getSeverityColor(injury.severity)}>
                        {injury.severity}/10
                      </Badge>
                    </div>
                  ))}
                </div>

                <div className="lead-meta mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <GeoAlt size={18} className="me-2 text-muted" />
                    <span>{lead.location.city}, {lead.location.state}</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <Clock size={18} className="me-2 text-muted" />
                    <span>Posted {timeAgo(lead.postedAt)}</span>
                  </div>
                </div>

                <div className="lead-value-display p-3 bg-light rounded mb-3">
                  <div className="text-muted small">Sell Price</div>
                  <div className="fs-3 fw-bold text-success">
                    {currencyFormatter.format(lead.estimatedValue)}
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger small mb-3">{error}</div>
                )}

                {isAdmin && lead.status !== 'Sold' && (
                  <Button variant="outline-danger" className="w-100" onClick={handleDelete} disabled={deleting}>
                    <Trash size={16} className="me-2" />
                    {deleting ? 'Deleting...' : 'Delete Lead'}
                  </Button>
                )}

                {!isAdmin && idToken && (
                  purchased ? (
                    <Button variant="success" className="w-100" disabled>
                      <CheckCircle size={18} className="me-2" />
                      Purchased
                    </Button>
                  ) : (
                    <Button
                      className="qmi-btn-primary w-100"
                      onClick={handlePurchase}
                      disabled={purchasing}
                    >
                      {purchasing ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CartPlus size={18} className="me-2" />
                          Buy This Lead
                        </>
                      )}
                    </Button>
                  )
                )}
              </Card.Body>
            </Card>

            <Card className="lead-contact-card">
              <Card.Body>
                <h5 className="mb-3">Contact Information</h5>

                <div className="d-flex align-items-center mb-2">
                  <Person size={18} className="me-2 text-muted" />
                  <span>{lead.contact.firstName} {lead.contact.lastName}</span>
                </div>
                <div className="d-flex align-items-center">
                  <Telephone size={18} className="me-2 text-muted" />
                  <a href={`tel:${lead.contact.phone}`} className="text-decoration-none">
                    {lead.contact.phone}
                  </a>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
